import {Alert} from 'platform/components';
import {Box, Grid, Show, Stack, VStack} from 'platform/foundation';

import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, type RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useAbcXyzDetailView} from '../hooks/useAbcXyzDetailView';
import type {AbcXyzTabProps} from '../types/AbcXyzTabProps';
import {AbcXyzClassCard} from './AbcXyzClassCard';
import {AbcXyzDetailGrid} from './AbcXyzDetailGrid';
import {AbcXyzIssuedQuantityChart} from './AbcXyzIssuedQuantityChart';

const OVERVIEW_GRID_CODE = 'report-abc-xyz-row-detail-overview';
const CLASS_CARD_COLUMNS = 3;
/** Preserve readable charts and grid rows when the viewport requires dialog scrolling. */
const CONTENT_MIN_HEIGHT_STEPS = 64;
const CHART_FLEX = 2;
const GRID_FLEX = 3;

export function AbcXyzOverviewTab(props: AbcXyzTabProps & RequiredTestIdProps) {
  const view = useAbcXyzDetailView(props.detail);

  return (
    <VStack spacing={2} height={['auto', 'auto', '100%']} minHeight={0}>
      <Box flexShrink={0}>
        <Grid columns={[1, CLASS_CARD_COLUMNS]} spacing={2}>
          <AbcXyzClassCard
            title={i18n.t('entity.warehouse.reporting.abcClass')}
            classLetter={props.detail.abcClass}
            name={view.abcName}
            hint={view.abcHint}
            colorScheme="green"
            data-testid={suffixTestId('abcClassCard', props)}
          />
          <AbcXyzClassCard
            title={i18n.t('entity.warehouse.reporting.xyzClass')}
            classLetter={props.detail.xyzClass}
            name={view.xyzName}
            hint={view.xyzHint}
            colorScheme="purple"
            data-testid={suffixTestId('xyzClassCard', props)}
          />
          <AbcXyzClassCard
            title={i18n.t('entity.warehouse.reporting.combinedClass')}
            classLetter={props.detail.abcXyzClass}
            name={view.combinedName}
            colorScheme="blue"
            data-testid={suffixTestId('combinedClassCard', props)}
          />
        </Grid>
      </Box>
      <Box flexShrink={0}>
        <Show when={view.dataQualityMessage}>
          {(message) => (
            <Alert
              type="inline"
              variant="warning"
              title={i18n.t('entity.warehouse.reporting.dataQualityWarning')}
              message={message}
              data-testid={suffixTestId('dataQualityWarning', props)}
            />
          )}
        </Show>
      </Box>
      <Box
        flex={[undefined, undefined, 1]}
        minHeight={[undefined, undefined, CONTENT_MIN_HEIGHT_STEPS]}
        minWidth={0}
      >
        <Stack
          direction={['column', 'column', 'row']}
          spacing={2}
          align="stretch"
          height={['auto', 'auto', '100%']}
        >
          <Box
            flex={[undefined, undefined, CHART_FLEX]}
            minWidth={0}
            height={[CONTENT_MIN_HEIGHT_STEPS, CONTENT_MIN_HEIGHT_STEPS, undefined]}
          >
            <AbcXyzIssuedQuantityChart
              detail={props.detail}
              title={i18n.t('entity.warehouse.reporting.issuedQuantityLast24Months', {
                unit: props.detail.unitName ?? EMPTY_PLACEHOLDER,
              })}
              isFullHeight
              data-testid={suffixTestId('chart', props)}
            />
          </Box>
          <Box
            flex={[undefined, undefined, GRID_FLEX]}
            minWidth={0}
            height={[CONTENT_MIN_HEIGHT_STEPS, CONTENT_MIN_HEIGHT_STEPS, undefined]}
          >
            <AbcXyzDetailGrid
              gridCode={OVERVIEW_GRID_CODE}
              articleId={props.articleId}
              data-testid={suffixTestId('overviewGrid', props)}
            />
          </Box>
        </Stack>
      </Box>
    </VStack>
  );
}
